import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import * as API from "services/api"
import {
  isNil,
  union,
  uniqBy,
  without,
} from "lodash-es"
import paginatedResourceList from "utils/paginatedResourceList"
import { getGroupingMethod, groupByIsOpen } from "TaskList/taskGroupingHelper"
import {
  getDataSortingMethod, ASSIGNEES_TYPE, FORM_TYPE, LONG_TEXT_TYPE, TEXT_TYPE, DUE_DATE_TYPE, MEMBERS_TYPE,
} from "shared/Table/TableDataElement/tableDataType"
import { errorToast } from "components/shared/toast"

const DUE_DATE_COLUMN = {
  label: "Due Date",
  value: "dueDate",
  sortable: true,
  width: "120px",
  dataType: DUE_DATE_TYPE,
}
const SLUG_COLUMN = {
  label: "Submission ID",
  value: "submissionSlug",
  sortable: true,
  width: "150px",
  dataType: TEXT_TYPE,
}
const TASK_NAME_COLUMN = {
  label: "Task Name",
  value: "title",
  sortable: true,
  width: "200px",
  dataType: TEXT_TYPE,
}
const SUBFORM_TITLE_COLUMN = {
  label: "Task Form",
  value: "subformTitle",
  sortable: true,
  width: "200px",
  dataType: TEXT_TYPE,
}
const FORM_COLUMN = {
  label: "Form",
  value: "form",
  sortable: true,
  width: "300px",
  dataType: FORM_TYPE,
}
const DESCRIPTION_COLUMN = {
  label: "Notes",
  value: "description",
  sortable: true,
  width: "200px",
  dataType: LONG_TEXT_TYPE,
};

const SUBMISSION_STATUS_COLUMN = {
  label: "Submission Status",
  value: "submissionStatus",
  sortable: true,
  dataType: TEXT_TYPE,
};

const RELATIONSHIP_TYPE_COLUMN = {
  value: "relationshipType",
}

const CATEGORY_COLUMN = {
  value: "category",
}

const ASSIGNEES_COLUMN = {
  label: "Assignees",
  value: "activeAssigneeNames",
  sortable: true,
  width: "50px",
  dataType: ASSIGNEES_TYPE,
}

const MEMBERS_COLUMN = {
  label: "Members",
  value: "activeMemberNames",
  sortable: true,
  width: "50px",
  dataType: MEMBERS_TYPE,
}

const DEFAULT_COLUMNS = [
  DUE_DATE_COLUMN,
  SLUG_COLUMN,
  FORM_COLUMN,
  SUBFORM_TITLE_COLUMN,
  TASK_NAME_COLUMN,
  DESCRIPTION_COLUMN,
  ASSIGNEES_COLUMN,
  MEMBERS_COLUMN,
  SUBMISSION_STATUS_COLUMN,
]

const DEFAULT_COLUMN_ORDER = DEFAULT_COLUMNS.map(({ value }) => value)

const INITIAL_STATE = {
  tasks: [],
  columns: DEFAULT_COLUMNS,
  columnOrder: DEFAULT_COLUMN_ORDER,
  hiddenColumns: [],
  groupByColumns: [RELATIONSHIP_TYPE_COLUMN, CATEGORY_COLUMN],
  currentSort: {
    column: SLUG_COLUMN,
    isAsc: true,
  },
  selectedGroupingColumn: RELATIONSHIP_TYPE_COLUMN,
  showOnlyClosedTasks: false,
  deselectedFormIds: [],
  forms: [],
  deselectedTaskCategoryIds: [],
  isLoading: true,
  isLoadingUnassignedTasks: false,
  unassignedTasksLoaded: false,
  paginationLoadedCount: 0,
  paginationTotalCount: 0,
};

const columnWithValue = (columns, columnValue) => columns.find((column) => column.value === columnValue)

// The order can hold values of columns that no longer exist, which are not displayed.
const orderedColumnValues = ({ columns, columnOrder }) => (
  columnOrder.filter((columnValue) => columnWithValue(columns, columnValue))
)

// Persisted settings store a column's value rather than the column itself, so that a
// column's definition can change without a stale copy of it being restored.
const buildCurrentSort = ({ columns }, { currentSort = {} }) => {
  const { columnValue, isAsc } = currentSort
  const column = columnWithValue(columns, columnValue)

  if (!column) return INITIAL_STATE.currentSort

  return { column, isAsc: isAsc ?? INITIAL_STATE.currentSort.isAsc }
}

const buildGroupingColumn = ({ groupByColumns }, { selectedGroupingColumnValue }) => (
  columnWithValue(groupByColumns, selectedGroupingColumnValue) ?? INITIAL_STATE.selectedGroupingColumn
)

// A column added since the settings were persisted is absent from both lists, which leaves it
// visible and at the end of the order.  Values of columns that no longer exist are harmless.
const buildColumnOrder = ({ columns }, { columnOrder = [] }) => (
  union(columnOrder, columns.map(({ value }) => value))
)

let incrementPaginationLoadedCount
let resetPaginationCounts
let setPaginationTotalCount

export const getTasks = createAsyncThunk(
  "taskTable/getTasks",
  async (_arg, thunkAPI) => {
    const { dispatch } = thunkAPI

    dispatch(resetPaginationCounts())

    const updateLoadingCounts = ({ paginationHeaders, pageResourceCount }) => {
      if (isNil(paginationHeaders.page)) return

      dispatch(setPaginationTotalCount(paginationHeaders.totalCount))
      dispatch(incrementPaginationLoadedCount(pageResourceCount))
    }

    const load = paginatedResourceList({
      apiFunction: API.getTasks,
      onPageLoad: updateLoadingCounts,
    })

    const tasks = await load()
    return tasks
  },
)

export const loadUnassignedTasks = createAsyncThunk(
  "taskTable/loadUnassignedTasks",
  async () => {
    const response = await API.unassignedTasks()

    if (!response.ok) {
      throw new Error("Something went wrong.  Unable to load unassigned tasks.")
    }

    return response.data
  },
)

export const taskTableSlice = createSlice({
  name: "taskTable",
  initialState: INITIAL_STATE,
  reducers: {
    incrementPaginationLoadedCount: (state, action) => {
      const { paginationLoadedCount } = state
      const newCount = paginationLoadedCount + action.payload
      state.paginationLoadedCount = newCount
    },
    initialize: (state, action) => {
      const { persistedSettings = {} } = action.payload

      const {
        deselectedFormIds = [],
        deselectedTaskCategoryIds = [],
        hiddenColumns = [],
        showOnlyClosedTasks = INITIAL_STATE.showOnlyClosedTasks,
      } = persistedSettings

      state.columnOrder = buildColumnOrder(state, persistedSettings)
      state.hiddenColumns = hiddenColumns
      state.currentSort = buildCurrentSort(state, persistedSettings)
      state.selectedGroupingColumn = buildGroupingColumn(state, persistedSettings)
      state.deselectedFormIds = deselectedFormIds
      state.deselectedTaskCategoryIds = deselectedTaskCategoryIds
      state.showOnlyClosedTasks = showOnlyClosedTasks
    },
    reorderColumns: (state, action) => {
      const { startIndex, endIndex } = action.payload

      const displayedColumnValues = orderedColumnValues(state)

      const valueToMove = displayedColumnValues[startIndex]
      const valueAtDesiredIndex = displayedColumnValues[endIndex]

      const desiredIndexInColumnOrder = state.columnOrder.indexOf(valueAtDesiredIndex)

      const newColumnOrder = state.columnOrder.filter((value) => value !== valueToMove)
      newColumnOrder.splice(desiredIndexInColumnOrder, 0, valueToMove)

      state.columnOrder = newColumnOrder
    },
    resetPaginationCounts: (state) => {
      state.paginationLoadedCount = 0
      state.paginationTotalCount = 0
    },
    setColumnHidden: (state, action) => {
      state.hiddenColumns = union(state.hiddenColumns, [action.payload])
    },
    setColumnVisible: (state, action) => {
      state.hiddenColumns = without(state.hiddenColumns, action.payload)
    },
    setCurrentSort: (state, action) => {
      const selectedColumn = action.payload
      if (state.currentSort.column.value === selectedColumn.value) {
        state.currentSort.isAsc = !state.currentSort.isAsc
      } else {
        state.currentSort = {
          column: selectedColumn,
          isAsc: true,
        }
      }
    },
    selectForm: (state, action) => {
      state.deselectedFormIds = without(state.deselectedFormIds, action.payload)
    },
    deselectForm: (state, action) => {
      state.deselectedFormIds = union(state.deselectedFormIds, [action.payload])
    },
    setPaginationCurrentCount: (state, action) => {
      state.paginationLoadedCount = action.payload
    },
    setPaginationTotalCount: (state, action) => {
      state.paginationTotalCount = action.payload
    },
    setSelectedGrouping: (state, action) => {
      const column = state.groupByColumns.find(({ value }) => value === action.payload)
      state.selectedGroupingColumn = column
    },
    setShowClosedTasks: (state, action) => {
      state.showOnlyClosedTasks = action.payload
    },
    selectTaskCategory: (state, action) => {
      state.deselectedTaskCategoryIds = without(state.deselectedTaskCategoryIds, action.payload)
    },
    deselectTaskCategory: (state, action) => {
      state.deselectedTaskCategoryIds = union(state.deselectedTaskCategoryIds, [action.payload])
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTasks.fulfilled, (state, action) => {
      state.tasks = action.payload
      const taskForms = state.tasks.map(({ form }) => ({ ...form }))
      state.forms = uniqBy(
        taskForms,
        ({ id }) => id,
      )

      state.isLoading = false
    })

    builder.addCase(loadUnassignedTasks.pending, (state) => {
      state.isLoadingUnassignedTasks = true
      state.unassignedTasksLoaded = false
    })

    builder.addCase(loadUnassignedTasks.fulfilled, (state, action) => {
      const { payload: unassignedTasks } = action

      state.tasks = [...state.tasks, ...unassignedTasks]

      const taskForms = state.tasks.map(({ form }) => ({ ...form }))
      state.forms = uniqBy(taskForms, "id")

      state.isLoadingUnassignedTasks = false
      state.unassignedTasksLoaded = true
    })

    builder.addCase(loadUnassignedTasks.rejected, (state, action) => {
      errorToast(action.error.message)
      state.isLoadingUnassignedTasks = false
      state.unassignedTasksLoaded = false
    })
  },
})

incrementPaginationLoadedCount = taskTableSlice.actions.incrementPaginationLoadedCount
resetPaginationCounts = taskTableSlice.actions.resetPaginationCounts
setPaginationTotalCount = taskTableSlice.actions.setPaginationTotalCount

export const {
  initialize,
  reorderColumns,
  setColumnHidden,
  setColumnVisible,
  setCurrentSort,
  setSelectedGrouping,
  setShowClosedTasks,
  selectForm,
  deselectForm,
  selectTaskCategory,
  deselectTaskCategory,
} = taskTableSlice.actions;

export const getTaskTable = (state) => state.taskTable

export const orderedColumns = (state) => {
  const { columns } = state.taskTable

  return orderedColumnValues(state.taskTable).map((columnValue) => columnWithValue(columns, columnValue))
}

export const visibleColumns = (state) => {
  const { hiddenColumns } = state.taskTable

  return orderedColumns(state).filter(({ value }) => !hiddenColumns.includes(value))
}

export const isColumnVisible = (columnValue) => (state) => !state.taskTable.hiddenColumns.includes(columnValue)

export const getGroupedTasks = (state) => {
  const {
    tasks,
    currentSort,
    deselectedFormIds,
    selectedGroupingColumn,
    deselectedTaskCategoryIds,
    showOnlyClosedTasks,
  } = state.taskTable

  let filteredTasks = tasks.filter(({ form, category }) => (
    !deselectedFormIds.includes(form.id) && !deselectedTaskCategoryIds.includes(category.id)
  ))

  let groupingMethod

  if (showOnlyClosedTasks) {
    filteredTasks = filteredTasks.filter(({ isOpen }) => isOpen === false)
    groupingMethod = () => groupByIsOpen(filteredTasks);
  } else {
    filteredTasks = filteredTasks.filter(({ isOpen }) => (isOpen))
    groupingMethod = getGroupingMethod(selectedGroupingColumn);
  }

  const sortingMethod = getDataSortingMethod(currentSort.column)
  const sortedTasks = sortingMethod(filteredTasks, currentSort)

  return groupingMethod(sortedTasks, selectedGroupingColumn)
}

export const getCurrentGrouping = (state) => state.taskTable.selectedGroupingColumn

export const isTaskCategoryVisible = (id) => (state) => !state.taskTable.deselectedTaskCategoryIds.includes(id)

export const getIsLoading = (state) => state.taskTable.isLoading

export const isLoadingUnassignedTasks = (state) => state.taskTable.isLoadingUnassignedTasks

export const unassignedTasksLoaded = (state) => state.taskTable.unassignedTasksLoaded

export const paginationTaskLoadedCount = (state) => (
  state.taskTable.paginationLoadedCount
)

export const paginationTaskTotalCount = (state) => (
  state.taskTable.paginationTotalCount
)

// The sort and grouping selections are persisted as column values, which initialize resolves
// back to the columns themselves.
export const settingsToPersist = (state) => {
  const {
    columnOrder,
    currentSort,
    deselectedFormIds,
    deselectedTaskCategoryIds,
    hiddenColumns,
    selectedGroupingColumn,
    showOnlyClosedTasks,
  } = state.taskTable

  return {
    columnOrder,
    currentSort: { columnValue: currentSort.column.value, isAsc: currentSort.isAsc },
    deselectedFormIds,
    deselectedTaskCategoryIds,
    hiddenColumns,
    selectedGroupingColumnValue: selectedGroupingColumn.value,
    showOnlyClosedTasks,
  }
}

export default taskTableSlice.reducer
